import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  CONSENT_CHANGE_EVENT,
  initializeMarketingConsent,
} from "@lib/utils/consentPreferences";

const GTM_DATA_LAYER = "dataLayer";
const GA_EVENT_NAMES = new Set([
  "sign_up",
  "sign_up_cta_click",
  "login",
  "pricing_view",
  "cite_checker_access_created",
  "cite_checker_access_load",
  "cite_checker_case_provision_error",
  "cite_checker_case_provision_start",
  "cite_checker_case_provision_success",
  "cite_checker_email_verified_redirect",
  "cite_checker_limit_modal_impression",
  "cite_checker_limit_modal_redirect",
  "cite_checker_limit_reached",
  "cite_checker_login_success",
  "cite_checker_modal_impression",
  "cite_checker_register_attempt",
  "cite_checker_register_success",
  "cite_checker_usage_recorded",
  "cite_checker_verification_resent",
  "citation_checker_critique_failed",
  "citation_checker_critique_started",
  "citation_checker_critique_success",
  "citation_checker_upload_failed",
  "citation_checker_upload_started",
  "citation_checker_upload_success",
  "account_chat_run_aborted",
  "account_chat_run_completed",
  "account_chat_run_failed",
  "workspace_task_completed",
  "workspace_case_assigned",
  "workspace_integration_connected",
  "workspace_agent_activated",
  "law_document_view",
]);
const SAFE_AUTH_METHODS = new Set(["email", "google", "microsoft"]);
const SAFE_EVENT_SOURCES = new Set([
  "cite_checker",
  "dashboard",
  "law_library",
  "public_page",
]);
const SAFE_WORKSPACE_INTEGRATIONS = new Set([
  "slack",
  "google_calendar",
  "microsoft_calendar",
]);
const SAFE_SIGNUP_CTA_SOURCES = new Set([
  "cases",
  "cite_checker",
  "law",
  "public_page",
]);
const SAFE_SIGNUP_CTA_PLACEMENTS = new Set([
  "navbar-signup",
  "navbar-signup-arrow",
  "navbar-signup-icon",
  "navbar-signup-text",
]);
const SAFE_PRICING_PATHS = new Set([
  "/pricing",
  "/pricing-cards",
  "/pro-se-price",
  "/small-firm",
  "/big-firm",
]);
const SAFE_PUBLIC_PATHS = new Set([
  ...SAFE_PRICING_PATHS,
  "/",
  "/watch",
  "/products",
  "/startups-vc",
  "/civil",
  "/criminal",
  "/cite-checker",
  "/product-documentation",
  "/product-documentation/faq-structured-data",
  "/faq",
  "/signup",
  "/book-a-demo",
  "/partnerships",
  "/contact",
  "/changelog",
  "/compare",
  "/compare/vienna-vs-cocounsel",
  "/compare/vienna-vs-harvey",
  "/compare/vienna-vs-lexis-plus-ai",
  "/terms-of-service",
  "/privacy-policy",
  "/login",
  "/forgot-password",
]);

const safePagePath = (value) => {
  if (typeof value !== "string") return "/other";
  const pathname = value.split(/[?#]/, 1)[0];
  if (!pathname.startsWith("/")) return "/other";
  if (SAFE_PUBLIC_PATHS.has(pathname)) return pathname;
  if (/^\/dashboard(?:\/|$)/.test(pathname)) return "/dashboard";
  if (/^\/law(?:\/|$)/.test(pathname)) return "/law";
  if (/^\/blog(?:\/|$)/.test(pathname)) return "/blog";
  return "/other";
};

const safePageTitle = (pagePath) => {
  if (pagePath === "/dashboard") return "Nougat Workspace";
  if (pagePath === "/law") return "Nougat Legal Library";
  if (pagePath === "/blog") return "Nougat Blog";
  if (SAFE_PRICING_PATHS.has(pagePath)) return "Nougat Pricing";
  return "Nougat";
};

// Every hit is recorded under nougat.law, whichever host served the page.
const ANALYTICS_ORIGIN = "https://nougat.law";

const safePageContext = (pagePath) => {
  const safePath = safePagePath(pagePath || window.location.pathname);
  return {
    page_location: `${ANALYTICS_ORIGIN}${safePath}`,
    page_path: safePath,
    page_referrer: ANALYTICS_ORIGIN,
    page_title: safePageTitle(safePath),
  };
};

const safeEventPayload = (eventName, payload) => {
  const eventPayload = payload && typeof payload === "object" ? payload : {};

  if (eventName === "sign_up" || eventName === "login") {
    const safe = {};
    if (SAFE_AUTH_METHODS.has(eventPayload.method)) safe.method = eventPayload.method;
    if (SAFE_EVENT_SOURCES.has(eventPayload.source)) safe.source = eventPayload.source;
    return safe;
  }

  if (eventName === "pricing_view") {
    return { page_path: safePagePath(eventPayload.page_path) };
  }

  if (eventName === "sign_up_cta_click") {
    const safe = {};
    if (SAFE_SIGNUP_CTA_SOURCES.has(eventPayload.source)) safe.source = eventPayload.source;
    if (SAFE_SIGNUP_CTA_PLACEMENTS.has(eventPayload.placement)) safe.placement = eventPayload.placement;
    return safe;
  }

  if (eventName === "virtual_page_view") {
    return { page_path: safePagePath(eventPayload.page_path) };
  }

  if (eventName === "workspace_integration_connected") {
    const safe = {};
    if (SAFE_EVENT_SOURCES.has(eventPayload.source)) safe.source = eventPayload.source;
    if (SAFE_WORKSPACE_INTEGRATIONS.has(eventPayload.integration)) {
      safe.integration = eventPayload.integration;
    }
    return safe;
  }

  if (GA_EVENT_NAMES.has(eventName)) {
    const safe = {};
    if (SAFE_EVENT_SOURCES.has(eventPayload.source)) safe.source = eventPayload.source;
    return safe;
  }

  return {};
};

const pushToDataLayer = (eventData) => {
  if (typeof window === "undefined") return;
  window[GTM_DATA_LAYER] = window[GTM_DATA_LAYER] || [];
  window[GTM_DATA_LAYER].push(eventData);
};

export const trackEvent = (eventName, payload = {}, eventOptions = {}) => {
  if (!eventName) return;
  if (typeof window === "undefined") return;

  const safePayload = safeEventPayload(eventName, payload);
  const eventPayload = GA_EVENT_NAMES.has(eventName)
    ? { ...safePageContext(safePayload.page_path), ...safePayload }
    : safePayload;
  pushToDataLayer({
    event: eventName,
    ...eventPayload,
  });

  // These named conversion events used to depend on a shared GTM container.
  // Send them directly to Nougat's GA4 property now that the container is
  // intentionally absent from this cross-brand origin.
  if (GA_EVENT_NAMES.has(eventName) && typeof window.gtag === "function") {
    const options = {};
    if (typeof eventOptions.event_callback === "function") {
      options.event_callback = eventOptions.event_callback;
    }
    if (Number.isFinite(eventOptions.event_timeout) && eventOptions.event_timeout > 0) {
      options.event_timeout = eventOptions.event_timeout;
    }
    window.gtag("event", eventName, { ...eventPayload, ...options });
  }
};

const trackGooglePageView = ({ pageLocation, pagePath, pageTitle }) => {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  // public/index.html hooks history directly and sends page_view for every route
  // change. This hook is currently unused, but if it is ever wired up it must not
  // double every in-app pageview.
  if (window.__juristaiSpaPageViewsEnabled) return;

  window.gtag("event", "page_view", {
    page_location: pageLocation,
    page_path: pagePath,
    page_referrer: ANALYTICS_ORIGIN,
    page_title: pageTitle,
  });
};

const isPricingRoute = (pathname = "") => {
  const normalized = pathname.toLowerCase();
  return (
    normalized === "/pricing" ||
    normalized === "/pricing-cards" ||
    normalized === "/pro-se-price" ||
    normalized === "/small-firm" ||
    normalized === "/big-firm"
  );
};

export const useMarketingTracking = () => {
  const location = useLocation();
  const user = useSelector((state) => state.user);
  const previousPathRef = useRef("");
  const pricingTrackedRef = useRef(new Set());
  const [canTrack, setCanTrack] = useState(true);

  useEffect(() => {
    initializeMarketingConsent();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncConsent = () => {
      previousPathRef.current = "";
      setCanTrack(true);
    };

    window.addEventListener(CONSENT_CHANGE_EVENT, syncConsent);

    return () => {
      window.removeEventListener(CONSENT_CHANGE_EVENT, syncConsent);
    };
  }, []);

  useEffect(() => {
    if (!canTrack) return;

    const userId = user?.cognito_user_id || user?.userId || null;
    if (!userId) return;

    pushToDataLayer({
      event: "set_user_id",
      user_id: userId,
    });
  }, [canTrack, user?.cognito_user_id, user?.userId]);

  useEffect(() => {
    if (!canTrack) return;

    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    if (previousPathRef.current === currentPath) return;
    previousPathRef.current = currentPath;

    const pagePath = safePagePath(location.pathname);
    const pageLocation =
      typeof window !== "undefined" ? `${ANALYTICS_ORIGIN}${pagePath}` : "";
    const pageTitle = safePageTitle(pagePath);

    trackEvent("virtual_page_view", {
      page_path: pagePath,
      page_location: pageLocation,
      page_title: pageTitle,
    });

    trackGooglePageView({
      pageLocation,
      pagePath,
      pageTitle,
    });

    if (
      isPricingRoute(location.pathname) &&
      !pricingTrackedRef.current.has(location.pathname)
    ) {
      pricingTrackedRef.current.add(location.pathname);
      trackEvent("pricing_view", {
        page_path: location.pathname,
      });
    }
  }, [canTrack, location.pathname, location.search, location.hash]);
};
